import { Flex, Select } from "@radix-ui/themes";
import { useHotkeys } from "react-hotkeys-hook";
import {
  selectSelectedGondola,
  setSelectedCameraSkuSection,
  setSelectedGondola,
} from "@/entities/planogram";
import { updateVideoStream } from "@/entities/player";
import { Gondola } from "@/shared/api";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import { GondolaSelect } from "./gondola-select";
import { PlanogramCanvas } from "./planogram-canvas";
import { Sidebar } from "./sidebar";
import { useCameraData } from "./useCameraData";

export function PlanogramViewer({
  renderVideo,
}: {
  renderVideo: React.ReactNode;
}) {
  const selectedGondola = useAppSelector(selectSelectedGondola);
  const { cameraSkuSections, gondolaSkuSections } =
    useCameraData(selectedGondola);

  return (
    <Flex className="grow h-[calc(100dvh-16rem)]">
      <Flex direction="column" className="grow">
        <Header />
        <Flex className="grow">
          <Flex direction="column" className="grow">
            <PlanogramCanvas
              cameraSkuSections={cameraSkuSections}
              renderVideo={renderVideo}
            />
          </Flex>
          <Sidebar gondolaSkuSections={gondolaSkuSections} />
        </Flex>
      </Flex>
    </Flex>
  );
}

function Header() {
  const dispatch = useAppDispatch();
  const selectedGondola = useAppSelector(selectSelectedGondola);
  const selectedCamera = useAppSelector(
    (state) => state.player.videoStream.cameraId
  );

  const onSelectCamera = (cameraId: string) => {
    dispatch(updateVideoStream({ cameraId }));
  };

  const onSelectSkuSectionId = (skuSectionId: string | null) => {
    dispatch(setSelectedCameraSkuSection(skuSectionId));
  };

  const onSelectGondola = (gondola: Gondola | null) => {
    dispatch(setSelectedGondola(gondola));
  };

  const relatedCameraIds = selectedGondola?.shelfTrackingCameras || [];

  const goToPreviousCamera = () => {
    if (!selectedCamera || relatedCameraIds.length === 0) return;

    const currentIndex = relatedCameraIds.findIndex(
      (camera) => camera.id === selectedCamera
    );

    const previousIndex =
      (currentIndex - 1 + relatedCameraIds.length) % relatedCameraIds.length;
    onSelectCamera(relatedCameraIds[previousIndex]?.id);
  };

  const goToNextCamera = () => {
    if (!selectedCamera || relatedCameraIds.length === 0) return;

    const currentIndex = relatedCameraIds.findIndex(
      (camera) => camera.id === selectedCamera
    );
    const nextIndex = (currentIndex + 1) % relatedCameraIds.length;
    onSelectCamera(relatedCameraIds[nextIndex]?.id);
  };

  useHotkeys("mod+[", goToPreviousCamera, { preventDefault: true });
  useHotkeys("mod+]", goToNextCamera, { preventDefault: true });

  const onValueChange = (value: Gondola | null) => {
    if (value) {
      onSelectCamera(value.shelfTrackingCameras[0]?.id);
    }

    if (value !== selectedGondola) {
      onSelectSkuSectionId(null);
    }

    onSelectGondola(value);
  };

  return (
    <header className="p-1 border-b border-[var(--gray-7)]">
      <Flex justify="end" align="center" gap="2">
        <GondolaSelect value={selectedGondola} onValueChange={onValueChange} />
        <Select.Root
          size="1"
          value={selectedCamera || ""}
          onValueChange={onSelectCamera}
        >
          <Select.Trigger placeholder="Select camera" />
          <Select.Content position="popper">
            <Select.Group>
              {!!selectedGondola?.shelfTrackingCameras.length &&
                selectedGondola.shelfTrackingCameras.map((camera) => (
                  <Select.Item key={camera.id} value={camera.id}>
                    {camera.id}
                  </Select.Item>
                ))}
            </Select.Group>
          </Select.Content>
        </Select.Root>
      </Flex>
    </header>
  );
}
